import { useCallback } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import { openChatPreferences } from "src/lib/openChatPreferences";
import {
  ConversationStarters as SharedConversationStarters,
  ConversationStartersContainer,
  DEFAULT_DESCRIPTION,
  DEFAULT_HEADING,
  defaultChatIcons,
  resolveImportConversationStarters,
  type ImportConversationStartersPayload,
  type StarterCTA,
} from "@sudowrite/shared-chat";

export type { ImportConversationStartersPayload };
export { resolveImportConversationStarters };

const CHAT_PERSONALIZATION_CTA: StarterCTA = {
  label: "Personalize chat",
  message: "",
  requiresWriteMode: false,
  icon: defaultChatIcons.personalization,
  opensChatPersonalization: true,
};

const NEW_PROJECT_CTAS: StarterCTA[] = [
  CHAT_PERSONALIZATION_CTA,
  {
    label: "Create my outline",
    message: "Let's work on creating my outline",
    requiresWriteMode: true,
    icon: defaultChatIcons.rewrite,
  },
  { label: "Discuss story ideas", message: "I'd like to discuss story ideas", requiresWriteMode: false },
  { label: "Brainstorm a character arc", message: "Let's brainstorm a character arc", requiresWriteMode: false },
];

const IN_PROGRESS_CTAS: StarterCTA[] = [
  CHAT_PERSONALIZATION_CTA,
  { label: "What happens next", message: "What happens next?", requiresWriteMode: false },
  { label: "Get feedback", message: "I'd like some feedback", requiresWriteMode: false },
];

interface ConversationStartersProps {
  isNewProject: boolean;
  currentMode: "write" | "chat";
  importConversationStarters?: ImportConversationStartersPayload | null;
  importReportDocumentId?: string | null;
  importConversationStartersDismissedForReport?: string | null;
  /** Label for write-mode CTAs when the user is not already in write mode (matches composer mode switcher). */
  writeModeBadgeLabel?: string;
  onStarterClick: (message: string, requiresWriteMode: boolean) => void;
}

export const ConversationStarters = ({
  isNewProject,
  currentMode,
  importConversationStarters,
  importReportDocumentId,
  importConversationStartersDismissedForReport,
  writeModeBadgeLabel = "Allow edits",
  onStarterClick,
}: ConversationStartersProps) => {
  const personalizedStarters = resolveImportConversationStarters(
    importConversationStartersDismissedForReport === importReportDocumentId ? null : importConversationStarters,
    importReportDocumentId,
    CHAT_PERSONALIZATION_CTA,
  );
  const heading = personalizedStarters?.heading ?? DEFAULT_HEADING;
  const description = personalizedStarters?.description ?? DEFAULT_DESCRIPTION;
  const ctas = personalizedStarters?.ctas ?? (isNewProject ? NEW_PROJECT_CTAS : IN_PROGRESS_CTAS);

  const handleSharedStarterClick = useCallback(
    (cta: StarterCTA) => {
      if (cta.opensChatPersonalization) {
        AppEventPublisher.agenticChat.conversationStarter(cta.label, false, isNewProject);
        openChatPreferences({ advanced: false, source: "conversation_starter" });
        return;
      }
      AppEventPublisher.agenticChat.conversationStarter(cta.label, cta.requiresWriteMode, isNewProject);
      AppEventPublisher.agenticChat.newChat("conversation_starter");
      onStarterClick(cta.message, cta.requiresWriteMode);
    },
    [isNewProject, onStarterClick],
  );

  return (
    <ConversationStartersContainer>
      <SharedConversationStarters
        heading={heading}
        description={description}
        ctas={ctas}
        currentMode={currentMode}
        writeModeBadgeLabel={writeModeBadgeLabel}
        icons={defaultChatIcons}
        onStarterClick={handleSharedStarterClick}
      />
    </ConversationStartersContainer>
  );
};
